// Klient API konta użytkownika. Sesja w ciasteczku + CSRF, jak w api/auth.js.
import { apiFetch } from './apiFetch'
const BASE = process.env.REACT_APP_ACCOUNT_API_URL || '/api/v1/account'

const readCookie = (name) => {
  const m = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`))
  return m ? decodeURIComponent(m[2]) : null
}

const request = async (path, { method = 'GET', body } = {}) => {
  const headers = { 'Content-Type': 'application/json' }
  if (method !== 'GET') {
    const csrf = readCookie('csrftoken')
    if (csrf) headers['X-CSRFToken'] = csrf
  }
  let res
  try {
    res = await apiFetch(`${BASE}${path}`, {
      method, headers, credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch {
    return { ok: false, error: 'Brak połączenia z serwerem. Spróbuj ponownie.' }
  }
  let data = null
  try { data = await res.json() } catch { data = null }
  if (!res.ok) {
    // 401 z django-ninja ma treść po angielsku („Unauthorized”): sesja wygasła albo wylogowano w innej karcie.
    const tekst = res.status === 401 ? 'Sesja wygasła. Zaloguj się ponownie i spróbuj jeszcze raz.' : data && data.detail
    return { ok: false, httpStatus: res.status, data, error: tekst || 'Nie udało się wykonać operacji.' }
  }
  return { ok: true, data }
}

export const getProfile = () => request('/me')
// Darmowy miesiąc Premium do czasu płatności online (2026-10-19): odpowiedź to profil jak z `/me`.
export const odbierzDarmowyDostep = () => request('/premium/odbierz', { method: 'POST' })
// Formularz „Zgłoś błąd” (2026-10-13): publiczny, działa też bez logowania. Mail do nas, bez zapisu w bazie.
export const zglosBlad = (dane) => request('/zgloszenie-bledu', { method: 'POST', body: dane })
// Logo firmy jako obrazek; `wersja` z profilu (`logo_wersja`) zmienia adres po każdej zmianie logo.
export const adresLogo = (wersja) => `${BASE}/logo?v=${wersja}`
export const updateProfile = (payload) => request('/me', { method: 'PATCH', body: payload })

// Wizytówka wykonawcy (profil dla klienta przy zleceniu, 2026-10-24; backend `accounts/wizytowka.py`). Zmienia konto
// główne albo administrator firmy; odpowiedzią zmian jest cała wizytówka.
export const getWizytowka = () => request('/firma/wizytowka')
export const dodajZdjecieRealizacji = (obraz, podpis = '') =>
  request('/firma/wizytowka/zdjecia', { method: 'POST', body: { obraz, podpis } })
export const podpiszZdjecieRealizacji = (id, podpis) =>
  request(`/firma/wizytowka/zdjecia/${id}`, { method: 'PUT', body: { podpis } })
export const zdjecieRealizacjiNaPoczatek = (id) => request(`/firma/wizytowka/zdjecia/${id}/na-poczatek`, { method: 'POST' })
export const usunZdjecieRealizacji = (id) => request(`/firma/wizytowka/zdjecia/${id}`, { method: 'DELETE' })
export const szukajWizytowkiGoogle = (q) => request(`/firma/wizytowka/google?q=${encodeURIComponent(q)}`)
export const ustawNazweWizytowki = (nazwa) => request('/firma/wizytowka/nazwa', { method: 'PUT', body: { nazwa } })
export const ustawWizytowkeGoogle = (placeId) => request('/firma/wizytowka/google', { method: 'PUT', body: { place_id: placeId } })

// Adresy z backendu (`logo_url` profilu wykonawcy) zaczynają się od `/api/v1/account`; gdy front ma API pod innym
// adresem (`REACT_APP_ACCOUNT_API_URL`, lokalnie), podmieniamy początek.
const PREFIKS_API_KONTA = '/api/v1/account'
export const adresZApiKonta = (url) =>
  typeof url === 'string' && url.startsWith(PREFIKS_API_KONTA) ? BASE + url.slice(PREFIKS_API_KONTA.length) : url

export const lookupNip = (nip) => request('/company/lookup', { method: 'POST', body: { nip } })
export const saveCompany = (company) => request('/company', { method: 'PUT', body: company })
export const leaveCompany = () => request('/company', { method: 'DELETE' })

// Zespół firmy (konta zespołowe, 2026-10-11): osoby, uprawnienia, zaproszenia z limitem kont.
export const getZespol = () => request('/zespol')
export const zaprosDoZespolu = (email, uprawnienia) =>
  request('/zespol/zaproszenia', { method: 'POST', body: { email, uprawnienia } })
// Plany (BE-04, 2026-10-19): konto główne wybiera, kto pracuje dalej, gdy zespół przekracza limit planu.
export const ustawAktywnych = (osoby) => request('/zespol/aktywne', { method: 'PUT', body: { osoby } })
export const ponowZaproszenie = (id) => request(`/zespol/zaproszenia/${id}/ponow`, { method: 'POST' })
export const cofnijZaproszenie = (id) => request(`/zespol/zaproszenia/${id}`, { method: 'DELETE' })
export const zmienUprawnienia = (id, uprawnienia) =>
  request(`/zespol/osoby/${id}`, { method: 'PUT', body: { uprawnienia } })
// `przepnijDo`: id konta, na które przechodzą klienci usuwanej osoby (2026-10-11); bez niego konto główne.
export const usunZZespolu = (id, przepnijDo = null) =>
  request(`/zespol/osoby/${id}${przepnijDo != null ? `?przepnij_do=${encodeURIComponent(przepnijDo)}` : ''}`, { method: 'DELETE' })
// Powiadomienia na platformie (dzwonek w pasku): liczone przez serwer z kart klientów, typy do wyboru.
export const getPowiadomienia = () => request('/powiadomienia')
// Prognoza na montaże (pulpit firmy): pośrednik do MET Norway w backendzie, `accounts/pogoda.py`.
export const getPogoda = (lat, lon) => request(`/pogoda?lat=${lat.toFixed(3)}&lon=${lon.toFixed(3)}`)
export const oznaczPrzeczytane = ({ klucze = [], wszystkie = false } = {}) =>
  request('/powiadomienia/przeczytane', { method: 'POST', body: { klucze, wszystkie } })
export const getUstawieniaPowiadomien = () => request('/powiadomienia/ustawienia')
export const zapiszUstawieniaPowiadomien = (wylaczone) =>
  request('/powiadomienia/ustawienia', { method: 'PUT', body: { wylaczone } })
// Karta osoby: dane, uprawnienia, jej klienci (gdy wolno je widzieć) i zdjęcie.
export const getOsobaZespolu = (id) => request(`/zespol/osoby/${id}`)
export const ustawZdjecieOsoby = (id, zdjecie) =>
  request(`/zespol/osoby/${id}/zdjecie`, { method: 'PUT', body: { zdjecie } })
// Wersja zdjęcia (z listy osób) zmienia adres po każdej zmianie, więc przeglądarka nie trzyma starego.
export const adresZdjeciaOsoby = (id, wersja) => `${BASE}/zespol/osoby/${id}/zdjecie?v=${wersja}`
// Strona `/dolacz/:token` z linku w mailu. Podgląd działa bez logowania.
export const getZaproszenie = (token) => request(`/zaproszenia/${encodeURIComponent(token)}`)
export const przyjmijZaproszenie = (token) =>
  request(`/zaproszenia/${encodeURIComponent(token)}/przyjmij`, { method: 'POST' })

export const listObserved = () => request('/observed')
export const addObserved = (item) => request('/observed', { method: 'POST', body: item })
export const removeObserved = (category, productId) =>
  request(`/observed/${category}/${productId}`, { method: 'DELETE' })
export const syncObserved = (items) => request('/observed/sync', { method: 'POST', body: { items } })

export const listKits = () => request('/kits')
export const saveKit = (kit) => request('/kits', { method: 'POST', body: kit })
export const deleteKit = (id) => request(`/kits/${id}`, { method: 'DELETE' })

// Teczki PME2: lista klientów (wspólna dla firmy) i dane jednej teczki. Zapis automatyczny z `/teczka-pme2`.
export const listTeczki = () => request('/teczki-pme2')
export const getTeczka = (id) => request(`/teczki-pme2/${id}`)
export const createTeczka = (teczka) => request('/teczki-pme2', { method: 'POST', body: teczka })
export const updateTeczka = (id, teczka) => request(`/teczki-pme2/${id}`, { method: 'PUT', body: teczka })
export const deleteTeczka = (id) => request(`/teczki-pme2/${id}`, { method: 'DELETE' })

// Klienci (prosty CRM firmy): karta klienta i narzędzia podpięte pod nią.
export const listKlienci = () => request('/klienci')
export const getKlient = (id) => request(`/klienci/${id}`)
export const createKlient = (klient) => request('/klienci', { method: 'POST', body: klient })
export const updateKlient = (id, klient) => request(`/klienci/${id}`, { method: 'PUT', body: klient })
export const deleteKlient = (id) => request(`/klienci/${id}`, { method: 'DELETE' })
export const zmienEtapKlienta = (id, status) => request(`/klienci/${id}/status`, { method: 'PATCH', body: { status } })
// Kto w zespole prowadzi klienta (2026-10-11): właściciel firmy albo osoba z `klienci_wszyscy`; `userId` to id konta.
export const zmienOpiekunaKlienta = (id, userId) => request(`/klienci/${id}/opiekun`, { method: 'PATCH', body: { user_id: userId } })
export const zapiszEtapy = (etapy) => request('/klienci/etapy', { method: 'PUT', body: { etapy } })
export const dodajNotatke = (id, tresc) => request(`/klienci/${id}/notatki`, { method: 'POST', body: { tresc } })
export const usunNotatke = (id, notatkaId) => request(`/klienci/${id}/notatki/${notatkaId}`, { method: 'DELETE' })
export const przypiszZestaw = (kitId, klientId) => request(`/kits/${kitId}/klient`, { method: 'PATCH', body: { klient_id: klientId } })

export const deleteAccount = (confirmEmail) =>
  request('/delete', { method: 'POST', body: { confirm_email: confirmEmail } })

// Preferencje doboru (PLAN_SEGMENTY §5): producenci falowników/magazynów/modułów
// + sklepy stałej współpracy z trybem any/prefer/only.
export const getKitPreferences = () => request('/kit-preferences')
export const updateKitPreferences = (prefs) =>
  request('/kit-preferences', { method: 'PUT', body: prefs })
export const getKitPreferenceOptions = () => request('/kit-preferences/options')

// Ustawienia wyceny — WYLACZNIE konto firmowe (backend zwraca 403 dla prywatnego).
// Marze, stawki montazu, VAT i logo do dokumentu „VoltValue Projekt".
export const getQuoteSettings = () => request('/quote-settings')
export const updateQuoteSettings = (ustawienia) =>
  request('/quote-settings', { method: 'PUT', body: ustawienia })

// Etykiety typów działalności — MUSZĄ zgadzać się z UserType w accounts/models.py.
// Rozjazd nie wysypie aplikacji, ale użytkownik zobaczy surowy klucz techniczny.
//
// ═══ DWIE GAŁĘZIE, NIGDY OBIE NARAZ ═══
// Pytanie „czym się zajmujesz" ma sens dopiero, gdy wiadomo, czy pyta się firmy,
// czy osoby prywatnej. Prywatnemu użytkownikowi lista ról rynkowych (dystrybutor,
// hurtownia…) nie mówi nic poza tym, że trafił nie tam, gdzie trzeba — dlatego
// dostaje jedno pytanie, które faktycznie zmienia podpowiedzi: czy ma już PV.

/** Konto prywatne — wybór POJEDYNCZY, opcje wykluczają się nawzajem. */
export const TYPY_PRYWATNE = [
  { value: 'has_pv', label: 'Mam instalację PV' },
  { value: 'no_pv', label: 'Nie mam instalacji PV' },
]

/** Konto firmowe — wybór WIELOKROTNY, role bywają łączone w jednej firmie. */
export const TYPY_FIRMOWE = [
  { value: 'sales_install', label: 'Firma sprzedażowo-instalacyjna' },
  { value: 'installer', label: 'Firma wykonawcza' },
  { value: 'distributor', label: 'Dystrybutor / hurtownia' },
  { value: 'reseller', label: 'Sklep / sprzedawca detaliczny' },
  { value: 'trader', label: 'Doradca sprzedażowy / grupa handlowa' },
  { value: 'other', label: 'Inne' },
]

// Wartości, których żaden formularz już nie proponuje, ale które siedzą na
// istniejących kontach. Potrzebne WYŁĄCZNIE do wyświetlenia etykiety — bez nich
// profil założony przed tą zmianą pokazywałby surowy klucz („investor").
const TYPY_LEGACY = [
  { value: 'service', label: 'Serwis i przeglądy' },
  { value: 'designer', label: 'Projektant / audytor energetyczny' },
  { value: 'investor', label: 'Inwestor / deweloper farm PV' },
  { value: 'individual', label: 'Osoba prywatna' },
]

export const USER_TYPES = [...TYPY_PRYWATNE, ...TYPY_FIRMOWE, ...TYPY_LEGACY]

/** Wartości z gałęzi prywatnej — do wyczyszczenia przy przejściu na firmę. */
export const WARTOSCI_PRYWATNE = TYPY_PRYWATNE.map((t) => t.value)

/**
 * Typy z gałęzi wybranej rodzajem konta. Pusta tablica, dopóki użytkownik nie
 * wskaże, czy rejestruje się jako osoba, czy jako firma — pytanie bez tego
 * kontekstu nie ma dobrej odpowiedzi.
 */
export const typyDlaKonta = (accountType) => {
  if (accountType === 'private') return TYPY_PRYWATNE
  if (accountType === 'company') return TYPY_FIRMOWE
  return []
}

// Podpisy pod etykietami. Leżą obok list, bo te same kafelki pokazujemy
// w dwóch miejscach — w ustawieniach konta i przy rejestracji — a rozjazd
// treści między nimi wyglądałby jak dwie różne listy odpowiedzi.
export const USER_TYPE_DESCRIPTIONS = {
  has_pv: 'Szukasz rozbudowy albo wymiany sprzętu',
  no_pv: 'Dopiero planujesz instalację',
  sales_install: 'Sprzedajecie i montujecie u siebie',
  installer: 'Montujecie na zlecenie, sprzęt daje zleceniodawca',
  distributor: 'Sprzedajesz hurtowo instalatorom',
  reseller: 'Prowadzisz sklep dla klientów końcowych',
  trader: 'Sprzedajesz instalacje, montaż zlecasz',
  other: 'Coś innego, opisz poniżej',
  service: 'Naprawy, przeglądy, części zamienne',
  designer: 'Dobierasz sprzęt, nie prowadzisz sprzedaży',
  investor: 'Budujesz farmy PV, inna skala zakupów',
  individual: 'Kupujesz dla siebie',
}

export const userTypeLabel = (value) =>
  (USER_TYPES.find((t) => t.value === value) || {}).label || value
